import type { IntelCompanyListItemDto } from '@@types/api.types';
import { CompanyLogoPreview } from '@components/shared/company-logo-preview.component';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table';
import { ColumnDef, SortingState, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { getCompanyTypeLabel } from '@utils/company.util';
import { displayLocation } from '@utils/functions.util';
import {
  ArrowDownWideNarrowIcon,
  ArrowUpDownIcon,
  ArrowUpNarrowWideIcon,
  BuildingIcon,
  Loader2Icon,
} from 'lucide-react';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

type IntelOrganizationsTableProps = {
  isLoading: boolean;
  onSortingChange: (sorting: SortingState) => void;
  organizations: IntelCompanyListItemDto[];
  sorting: SortingState;
};

export const IntelOrganizationsTable: FC<IntelOrganizationsTableProps> = (props) => {
  const { isLoading, onSortingChange, organizations, sorting } = props;
  const navigate = useNavigate();

  const tableColumns = useMemo<ColumnDef<IntelCompanyListItemDto>[]>(
    () => [
      {
        accessorFn: (row) => row.name,
        cell: ({ row }) => {
          const organization = row.original;

          return (
            <div className="flex min-w-0 items-center gap-3">
              <CompanyLogoPreview
                className="h-9 w-9 rounded-lg"
                fallback={<BuildingIcon className="size-4 text-gray-600" />}
                logoAlt={`${organization.name} logo`}
                logoUrl={organization.logoUrl}
              />
              <span className="truncate text-sm font-medium text-gray-900">{organization.name}</span>
            </div>
          );
        },
        header: 'Organization',
        id: 'name',
        sortingFn: 'text',
      },
      {
        accessorFn: (row) => row.type,
        cell: ({ row }) => <span className="text-sm text-gray-900">{getCompanyTypeLabel(row.original.type)}</span>,
        header: 'Type',
        id: 'type',
        sortingFn: 'text',
      },
      {
        accessorFn: (row) => displayLocation({ city: row.city, countryCode: row.country, state: row.state }),
        cell: ({ row }) => (
          <span className="text-sm text-gray-900">
            {displayLocation({
              city: row.original.city,
              countryCode: row.original.country,
              state: row.original.state,
            }) || '—'}
          </span>
        ),
        header: 'Location',
        id: 'location',
        sortingFn: 'text',
      },
      {
        accessorFn: (row) => row.technologiesCount,
        cell: ({ row }) => <span className="text-sm text-gray-900">{row.original.technologiesCount}</span>,
        header: 'Technologies',
        id: 'technologiesCount',
        sortingFn: 'basic',
      },
      {
        accessorFn: (row) => row.productsCount,
        cell: ({ row }) => <span className="text-sm text-gray-900">{row.original.productsCount}</span>,
        header: 'Products',
        id: 'productsCount',
        sortingFn: 'basic',
      },
    ],
    [],
  );

  const table = useReactTable({
    columns: tableColumns,
    data: organizations,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    onSortingChange,
    state: { sorting },
  });

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center rounded-xl border">
        <Loader2Icon className="size-6 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                if (header.isPlaceholder) {
                  return <TableHead key={header.id} />;
                }

                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();

                return (
                  <TableHead
                    key={header.id}
                    className="text-xs font-semibold text-gray-500"
                  >
                    {canSort ? (
                      <button
                        className="flex items-center gap-1 text-xs font-semibold text-gray-600"
                        onClick={header.column.getToggleSortingHandler()}
                        type="button"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {sorted === 'asc' && <ArrowUpNarrowWideIcon className="size-3.5" />}
                        {sorted === 'desc' && <ArrowDownWideNarrowIcon className="size-3.5" />}
                        {!sorted && <ArrowUpDownIcon className="size-3.5 text-gray-400" />}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/intel/organizations/${row.original.id}`)}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                className="h-24 text-center text-sm text-gray-500"
                colSpan={tableColumns.length}
              >
                No Organizations found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
